import { Button, EchartsWithTheme, Empty, Modal, useTheme } from '@maxtropy/components';
import dayjs, { Dayjs } from 'dayjs';
import React, { useContext, useMemo, useState } from 'react';
import { DateType } from '../../const';
import { buildMonthCycleParams, getTimeRangeByMonthPeriod } from '../../utils';
import styles from './index.module.scss';
import { useRequest } from 'ahooks';
import { apiV2EnergyReportTrendPost, apiV2EnergyReportWaterDetailPost } from '@maxtropy/device-customer-apis-v2';
import { getWaterCostTrendChartOption, getWaterChartOption } from './charts';
import CardBox from '../CardBox';
import { MonthPeriodContext } from '../..';

type RangeValue = [Dayjs | null, Dayjs | null] | null;

const WATER = 3;

interface WaterProps {
  timeResolution: DateType;
  dateValue?: RangeValue;
}

const Water: React.FC<WaterProps> = ({ timeResolution, dateValue }) => {
  const theme = useTheme();
  const [open, setOpen] = useState<boolean>(false);
  const { reportScope, monthPeriodType, monthPeriodStart, monthPeriodEnd, monthCycleApiParams } =
    useContext(MonthPeriodContext);

  // 用水详情
  const { data: waterData, loading } = useRequest(
    async () => {
      let fromTime: number;
      let toTime: number;

      if (timeResolution === DateType.DAY) {
        // 按日查询：直接使用用户选择的日期范围
        fromTime = dateValue?.[0]?.startOf('day').valueOf() ?? 0;
        toTime = dateValue?.[1]?.endOf('day').valueOf() ?? 0;
      } else {
        // 按月/按年查询：使用月周期计算
        const timeRange = getTimeRangeByMonthPeriod(
          timeResolution!,
          dateValue?.[0],
          dateValue?.[1],
          monthPeriodType,
          monthPeriodStart,
          monthPeriodEnd
        );
        fromTime = timeRange.startTime;
        toTime = timeRange.endTime;
      }

      return await apiV2EnergyReportWaterDetailPost({
        fromTime,
        toTime,
        timeResolution: timeResolution,
        ...buildMonthCycleParams(timeResolution, monthCycleApiParams, reportScope),
      });
    },
    {
      ready: !!timeResolution && !!dateValue,
      refreshDeps: [timeResolution, dateValue, reportScope, monthCycleApiParams],
      debounceWait: 100,
    }
  );

  // 历史成本趋势
  const { data: costTrendData, loading: trendLoading } = useRequest(
    async () => {
      const endDate = dayjs(dateValue?.[1]);
      const startDate = endDate.subtract(2, 'year').startOf('year');

      return await apiV2EnergyReportTrendPost({
        type: WATER,
        fromTime: startDate.valueOf(),
        toTime: endDate.endOf('year').valueOf(),
        timeResolution: 'DAY',
        deptOrArea: reportScope === 'dept' ? 1 : reportScope === 'area' ? 2 : undefined,
        deptAreaId: monthCycleApiParams?.deptAreaId,
        ...buildMonthCycleParams(DateType.DAY, monthCycleApiParams, reportScope),
      });
    },
    {
      ready: open && !!timeResolution && !!dateValue && !!dateValue?.[1],
      refreshDeps: [timeResolution, dateValue, open],
      debounceWait: 100,
    }
  );

  // 图表
  const elecChartOption = useMemo(() => {
    return getWaterChartOption(waterData, timeResolution);
  }, [waterData, timeResolution]);

  // 历史趋势图表
  const costTrendChartOption = useMemo(() => {
    return getWaterCostTrendChartOption(costTrendData);
  }, [costTrendData]);

  return (
    <>
      <CardBox
        title="用水详情"
        extraContent={
          <div className={styles.time_box}>
            <Button type="primary" onClick={() => setOpen(true)}>
              历史成本趋势对比
            </Button>
          </div>
        }
      >
        <div className={styles.chart_wrapper}>
          <EchartsWithTheme
            empty={{
              isShowEmpty: !waterData || waterData?.list?.length === 0,
              emptyNode: <Empty />,
            }}
            loading={loading}
            theme={theme}
            style={{ height: 300 }}
            option={elecChartOption}
          />
        </div>
      </CardBox>
      <Modal
        title="历史成本趋势对比"
        open={open}
        width="80%"
        mask={false}
        style={{ top: '10%', minWidth: 900 }}
        onOk={() => {}}
        footer={null}
        onCancel={() => {
          setOpen(false);
        }}
      >
        <div style={{ marginTop: 16, height: '70vh', width: '100%' }}>
          <EchartsWithTheme
            theme={theme}
            loading={trendLoading}
            empty={{
              isShowEmpty: !costTrendData || costTrendData.list?.length === 0,
              emptyNode: <Empty className={styles.empty_box} />,
            }}
            style={{ height: '100%', marginTop: 16 }}
            option={costTrendChartOption}
          />
        </div>
      </Modal>
    </>
  );
};

export default Water;
